// Converts Telegram MarkdownV2-style formatting to HTML for Vue display
// Supports: *bold*, _italic_, `code`, ```pre```, [text](url), ![alt](url), ~strikethrough~

function escapeHtml(text) {
  if (!text) return ''
  // Only escape characters that are dangerous in HTML content
  // Single quotes (') don't need to be escaped in HTML content, only in attributes
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
  }
  return text.replace(/[&<>]/g, m => map[m])
}

// Escape HTML for use in attributes (needs quotes escaped)
function escapeHtmlAttr(text) {
  if (!text) return ''
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }
  return text.replace(/[&<>"']/g, m => map[m])
}

export function parseTelegramMarkdown(text, options = {}) {
  if (!text) return ''

  let html = String(text)

  // First escape any existing HTML to prevent XSS
  html = escapeHtml(html)

  // Order matters! Process more specific patterns first

  // Pre-formatted code blocks (```code```) - must be before inline code
  html = html.replace(/```([^`]+)```/g, (match, codeContent) => {
    return `<pre><code>${escapeHtml(codeContent.trim())}</code></pre>`
  })

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, (match, codeContent) => {
    return `<code>${escapeHtml(codeContent)}</code>`
  })

  // Bold (**text** or *text*) - process ** first
  html = html.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
  html = html.replace(/\*([^*]+)\*/g, '<b>$1</b>')

  // Underline (__text__) - must be before italic to avoid conflicts
  html = html.replace(/__([^_]+)__/g, '<u>$1</u>')

  // Italic (_text_)
  html = html.replace(/_([^_]+)_/g, '<i>$1</i>')

  // Strikethrough (~text~)
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>')

  // Spoiler (||text||)
  html = html.replace(/\|\|([^|]+)\|\|/g, '<span class="spoiler">$1</span>')

  // Images ![alt](url)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, altText, imageUrl) => {
    const normalizedUrl = imageUrl.replace(/&amp;/g, '&')
    const escapedUrl = escapeHtmlAttr(normalizedUrl)
    const escapedAlt = escapeHtmlAttr(altText)
    return `<img src="${escapedUrl}" alt="${escapedAlt}" class="inline-attachment-image">`
  })

  // Links [text](url)
  // Process after inline formatting to avoid underscore formatting corrupting
  // generated anchor attributes like target="_blank" or URLs with underscores.
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, linkUrl) => {
    const normalizedUrl = linkUrl.replace(/&amp;/g, '&')
    const escapedUrl = escapeHtmlAttr(normalizedUrl)
    return `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
  })

  // Quotations (> text)
  // Match lines starting with > and convert to blockquote
  html = html.replace(/^(&gt;|>)\s+(.+)$/gm, '<blockquote>$2</blockquote>')

  // Line breaks
  html = html.replace(/\n/g, '<br>')

  // Process task mentions (#task-id) at the end, if enabled
  // This should be done after all markdown processing
  if (options.processTaskMentions !== false) {
    html = processTaskMentions(html, options.taskMap || {}, options.buildTaskUrl)
  }

  html = processUserMentions(html)

  return html
}

function splitProtectedHtml(html) {
  const parts = []
  let lastIndex = 0
  const protectedPattern = /<(code|pre|a)\b[^>]*>[\s\S]*?<\/\1>|<img\b[^>]*>/gi

  let match
  while ((match = protectedPattern.exec(html)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: html.substring(lastIndex, match.index),
        isProtected: false,
      })
    }

    parts.push({
      text: match[0],
      isProtected: true,
    })

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < html.length) {
    parts.push({
      text: html.substring(lastIndex),
      isProtected: false,
    })
  }

  return parts
}

function processUserMentions(html) {
  return splitProtectedHtml(html)
    .map(part => {
      if (part.isProtected) return part.text

      return part.text.replace(
        /(^|[\s([{>])(@[a-zA-Z0-9_]{2,32})(?=$|[^a-zA-Z0-9_])/g,
        (_match, prefix, mention) => `${prefix}<span class="user-mention-text">${mention}</span>`
      )
    })
    .join('')
}

/**
 * Process task mentions (#task-id) and convert them to links
 * @param {string} html - HTML text (already processed by markdown)
 * @param {Object} taskMap - Map of taskIid -> { iid, id, title, projectId }
 * @param {Function} buildTaskUrl - Function to build task URL
 * @returns {string} - HTML with task mentions replaced by links
 */
function processTaskMentions(html, taskMap = {}, buildTaskUrl = null) {
  // Avoid processing inside code blocks and existing links.
  const parts = splitProtectedHtml(html)

  // Process each part
  return parts
    .map(part => {
      if (part.isProtected) {
        return part.text // Keep code blocks as-is
      }

      // Process task mentions in non-code text
      const pattern = /#(\d+)/g
      return part.text.replace(pattern, (match, taskIdStr) => {
        const taskId = parseInt(taskIdStr, 10)
        if (taskId <= 0) return match

        const task = taskMap[taskId]
        const defaultUrl = buildTaskUrl ? buildTaskUrl(taskId, null) : `#task-${taskId}`

        if (!task) {
          // Task not found, but still make it a link
          return `<a href="${escapeHtmlAttr(defaultUrl)}" class="task-mention-link" data-task-id="${taskId}">#${taskId}</a>`
        }

        const taskIid = task.iid
        if (!taskIid) return match
        const url = buildTaskUrl ? buildTaskUrl(taskIid, task.projectId) : defaultUrl
        const displayText = task.title ? `#${taskIid} ${escapeHtml(task.title)}` : `#${taskIid}`

        return `<a href="${escapeHtmlAttr(url)}" class="task-mention-link" data-task-id="${taskIid}" data-project-id="${task.projectId || ''}">${displayText}</a>`
      })
    })
    .join('')
}
